import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { AlertCircle, Send } from 'lucide-react';

interface CommentFormProps {
  sequenceId: string;
  sequenceName?: string;
  onSuccess: () => void;
  onCancel: () => void;
  parentCommentId?: string; // For threading/replies
}

export const CommentForm: React.FC<CommentFormProps> = ({
  sequenceId,
  sequenceName,
  onSuccess,
  onCancel,
  parentCommentId
}) => {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const maxLength = 2000;
  const minLength = 1;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setError('You must be signed in to comment');
      return;
    }

    const trimmedContent = content.trim();

    if (trimmedContent.length < minLength) {
      setError('Comment cannot be empty');
      return;
    }

    if (trimmedContent.length > maxLength) {
      setError(`Comment must be ${maxLength} characters or less`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('sequence_comments')
        .insert({
          sequence_id: sequenceId,
          user_id: user.id,
          content: trimmedContent,
          parent_comment_id: parentCommentId || null
        });

      if (insertError) throw insertError;

      onSuccess();
    } catch (err) {
      console.error('Error submitting comment:', err);
      setError('Failed to submit comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setContent('');
    setError(null);
    onCancel();
  };

  const remainingChars = maxLength - content.length;
  const isValid = content.trim().length >= minLength && content.length <= maxLength;

  return (
    <div className="space-y-4">
      <div className="flex items-start space-x-3">
        <Avatar className="w-8 h-8">
          <AvatarImage
            src={user?.user_metadata?.avatar_url}
            alt={user?.user_metadata?.full_name || user?.name || 'User'}
          />
          <AvatarFallback>
            {(user?.user_metadata?.full_name || user?.name || 'U')[0].toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="mb-2">
            <span className="text-sm font-medium">
              {user?.user_metadata?.full_name ||
               user?.user_metadata?.display_name ||
               user?.user_metadata?.username ||
               user?.name ||
               'Anonymous'}
            </span>
            {parentCommentId && (
              <span className="text-sm text-gray-500 ml-2">
                • Replying to comment
              </span>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={
              parentCommentId
                ? "Write your reply..."
                : `Share your thoughts about ${sequenceName || 'this sequence'}...`
            }
            className="min-h-[100px] resize-none"
            disabled={isSubmitting}
            maxLength={maxLength}
          />
          <div className="flex justify-between items-center mt-2">
            <div className="text-xs text-gray-500">
              {remainingChars < 100 && (
                <span className={remainingChars < 0 ? 'text-red-500' : 'text-orange-500'}>
                  {remainingChars} characters remaining
                </span>
              )}
            </div>
            <div className="text-xs text-gray-400">
              {content.length}/{maxLength}
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-center space-x-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!isValid || isSubmitting}
            className="min-w-[100px]"
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Posting...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Send className="w-4 h-4" />
                <span>{parentCommentId ? 'Reply' : 'Comment'}</span>
              </div>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CommentForm;